import {memo, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useWizard} from 'react-use-wizard';
import {Palette} from 'hubbl-shared';
import {
  ManagedInput,
  PaddingBottom,
  TextHeading1,
  TextHeading6,
  TextHeading7,
} from '../../../../../../../components';
import {
  useAppDispatch,
  useAppSelector,
  useI18n,
} from '../../../../../../../hooks';
import {GetStartedActions} from '../../../../../../../store/get-started/GetStartedActions';
import {GetStartedSelector} from '../../../../../../../store/get-started/GetStartedSelector';
import {UserSelector} from '../../../../../../../store/user/UserSelector';
import {GetStartedWizardFooter} from '../../footer/GetStartedWizardFooter';
import {
  GetStartedWizardStepContainer,
  GetStartedWizardStepContainerProps,
} from '../../step-container/GetStartedWizardStepContainer';

const FULL_NAME_FIELD = 'FULL_NAME_FIELD';
const COMPANY_NAME_FIELD = 'COMPANY_NAME_FIELD';

export const Step1 = memo(
  ({previousStep}: GetStartedWizardStepContainerProps) => {
    const {control, handleSubmit, setValue} = useForm();

    const userFullName = useAppSelector(GetStartedSelector.getUserFullName);
    const writtenCompanyName = useAppSelector(
      GetStartedSelector.getWrittenCompanyName,
    );
    const userDetails = useAppSelector(UserSelector.getUserDetails);
    const t = useI18n();

    // Check if user already has a full name (from Google sign-up)
    const existingFullName = userDetails?.fullName || '';
    const isNameFromAuth = existingFullName.length > 0;

    const {nextStep} = useWizard();
    const dispatch = useAppDispatch();

    useEffect(() => {
      // Use existing full name from user details if available, otherwise use wizard state
      const nameToUse = isNameFromAuth ? existingFullName : userFullName;
      setValue(FULL_NAME_FIELD, nameToUse);
      setValue(COMPANY_NAME_FIELD, writtenCompanyName);

      // If name is from auth (Google), also update the wizard state
      if (isNameFromAuth && !userFullName) {
        dispatch(GetStartedActions.setUserFullName(existingFullName));
      }

      // Set restricted mail domains from user's email domain
      if (userDetails?.email) {
        const emailDomain = userDetails.email.split('@')[1];
        if (emailDomain) {
          dispatch(GetStartedActions.setRestrictedMailDomains([emailDomain]));
        }
      }
    }, [
      setValue,
      userFullName,
      writtenCompanyName,
      existingFullName,
      isNameFromAuth,
      dispatch,
      userDetails?.email,
    ]);

    const onSubmit = (data: any) => {
      // Use existing full name if it's from auth (Google), otherwise use the form value
      const fullName = isNameFromAuth
        ? existingFullName
        : (data[FULL_NAME_FIELD] ?? '');
      const companyName = data[COMPANY_NAME_FIELD] ?? '';

      dispatch(GetStartedActions.setUserFullName(fullName));
      dispatch(GetStartedActions.setWrittenCompanyName(companyName));
      nextStep();
    };

    return (
      <GetStartedWizardStepContainer previousStep={previousStep}>
        <TextHeading1 center text={t.scenes.getStarted.setupYourCompany} />
        <PaddingBottom amount={12} />
        <TextHeading6
          text={t.scenes.getStarted.setupYourCompanyDescription}
          color={Palette.almostBlack500}
          center
        />
        <PaddingBottom amount={24} />

        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{width: '100%', maxWidth: '450px'}}>
          <TextHeading7 text={t.scenes.getStarted.yourFullName} />
          <PaddingBottom amount={8} />
          <ManagedInput
            control={control}
            controlKey={FULL_NAME_FIELD}
            placeholder="John Doe"
            isRequiredMessage={t.auth.cannotBeLeftBlank}
            isDisabled={isNameFromAuth}
            isCompact
          />

          <PaddingBottom amount={16} />

          <TextHeading7 text={t.scenes.getStarted.companyName} />
          <PaddingBottom amount={8} />
          <ManagedInput
            control={control}
            controlKey={COMPANY_NAME_FIELD}
            placeholder="Acme Inc"
            isRequiredMessage={t.auth.cannotBeLeftBlank}
            isCompact
          />
        </form>

        <div style={{width: '100%', maxWidth: '450px'}}>
          <GetStartedWizardFooter
            isBackHidden
            onCustomNextStep={handleSubmit(onSubmit)}
          />
        </div>
      </GetStartedWizardStepContainer>
    );
  },
);
